import { RegisterOptions } from "react-hook-form";
import { LINKEDIN_URL_PATTERN_REG_EXP } from "../../../constants"; // Adjust the import path if necessary.
import { ValidationProps } from "./FormFields.interfaces";

// Define the validation rules for different fields
export const getValidationRules = (
  validation: ValidationProps,
  fieldName: string,
  options?: { value: string }[],
  watch?: (field: string) => void | string
): RegisterOptions => {
  let maxLengthMessage: string;
  let minLengthMessage: string;

  if (
    validation?.lastName ||
    validation?.firstName ||
    validation?.email ||
    validation?.numeric
  ) {
    maxLengthMessage = `Your ${fieldName} cannot be more than '${validation?.maxLength}' characters long`;
    minLengthMessage = `Your ${fieldName} must be at least '${validation?.minLength}' characters long`;
  } else if (validation?.textarea) {
    maxLengthMessage = `Your message is longer than the ${validation?.maxLength}-character limit. Please make it shorter`;
    minLengthMessage = `Your message is shorter than the ${validation?.minLength}-character limit. Please make it longer`;
  } else {
    maxLengthMessage = `The field ${fieldName} must be a string or array type with a maximum length of '${validation?.maxLength}'`;
    minLengthMessage = `The field ${fieldName} must be a string or array type with a minimum length of '${validation?.minLength}'`;
  }
  return {
    ...(validation?.isLinkedIn && {
      validate: {
        validate: (value) => {
          return value || validation.dependency?.name
            ? true
            : `CV or LinkedIn profile is required`;
        },
      },
      pattern: {
        value: LINKEDIN_URL_PATTERN_REG_EXP,
        message: `Please enter a valid ${fieldName}`,
      },
    }),
    ...(!options &&
      validation?.isRequired && {
        required: `${fieldName} is required`,
      }),
    ...(options &&
      validation?.isRequired && {
        required: `You have to select an option`,
      }),
    ...(validation?.maxLength && {
      maxLength: { value: validation?.maxLength, message: maxLengthMessage },
    }),
    ...(validation?.minLength && {
      minLength: {
        value: validation?.minLength,
        message: minLengthMessage,
      },
    }),
    ...(validation?.range && {
      min: {
        value: validation?.range?.min,
        message: `The field ${fieldName} must be between ${validation?.range?.min} and ${validation.range.max}.`,
      },
      max: {
        value: validation?.range?.max,
        message: `The field ${fieldName} must be between ${validation?.range?.min} and ${validation.range.max}.`,
      },
    }),
    ...(validation?.alphaNumeric && {
      pattern: {
        value: /^[a-zA-Z0-9_ ]+$/,
        message: `Please enter a valid ${fieldName}`,
      },
    }),
    ...(validation?.numeric && {
      pattern: {
        value: /^[0-9]*$/,
        message: `The field ${fieldName} must contain only numbers`,
      },
    }),
    ...(validation?.email && {
      pattern: {
        value:
          /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
        message: "Please enter a valid email (e.g., user@example.com)",
      },
    }),
    ...(validation?.confirmEmail && {
      validate: (value: string) => {
        if (watch("email") !== value) {
          return "Your new email do not match";
        }
      },
    }),
    ...(validation?.password && {
      pattern: {
        value: /^(?=.*[A-Z])(?=.*[a-z])(?=.*\d).{12,}$/,
        message:
          "Your password must contain: At least 12 characters, Lower case letters (a-z), Upper case letters (A-Z), Numbers(0-9)",
      },
    }),
    ...(validation?.confirmPassword && {
      validate: (value: string) => {
        if (watch("password") !== value) {
          return "Your passwords do not match";
        }
      },
    }),
    ...(validation?.telephone && {
      pattern: {
        value: /^[+()]*[ 0-9]+$/,
        message: `Telephone number: Only numbers, brackets and + allowed`,
      },
    }),
    ...(validation?.stringValidator && {
      pattern: {
        value: /^[a-z ,.'-]+$/i,
        message: `Please enter a valid ${fieldName}`,
      },
    }),
    ...(validation?.integerValidator && {
      pattern: {
        value: /^[0-9]+$/,
        message: `The field ${fieldName} must be a number`,
      },
    }),
    ...(validation?.decimalValidator && {
      pattern: {
        value: /^\d+([.]\d{1,2})?$/,
        message: `The field ${fieldName} must be a valid number`,
      },
    }),
    ...(validation?.eitherOrRequired && {
      validate: (value: string) => {
        const otherFieldValue = watch(validation?.eitherOrRequired?.fieldName);
        if (!value && !otherFieldValue) {
          return `Either ${fieldName} or ${validation?.eitherOrRequired?.fieldLabel} is required`;
        }
      },
    }),
  };
};
